import { Box, Button, CircularProgress, ITheme, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AUTHENTICATED_ROUTES } from '../../resources/routes-constants';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/reducers/store';
import { loadPortfolioTracking } from '../../store/actions/portfolioTracking';
import { PortfolioTrackEventType } from '../../models/Portfolio';
import { initEdit } from '../../store/actions/createPortfolio';
import FundSummaryContent from '../../components/createPortfolio/fundSummary/FundSummaryContent';
import { loadListOfFunds } from '../../store/actions/libraryOfFunds';
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { useTheme } from '@emotion/react';
import BackButton from '../../components/common/BackButton';

const PortfolioSummaryPage: React.FC = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tracking = useAppSelector(state => state.portfolioTracking.portfolio);
    const portfolio = useAppSelector(state => state.createPortfolio.portfolio);
    const funds = useAppSelector(state => state.libraryOfFundsReducer.funds);
    const dispatch = useAppDispatch();
    const [init, setInit] = useState(false);
    const theme = useTheme() as ITheme;

    if (id === undefined){
      navigate(-1);
    }

    useEffect(() => {
        if (tracking === undefined || tracking.id !== id) {
            dispatch(loadPortfolioTracking(id as string));
        }
        if (portfolio === undefined || portfolio.id !== id) {
            dispatch(initEdit(id as string))
        }
        if (funds === undefined) {
            dispatch(loadListOfFunds());
        }
    }, [dispatch, id]);

    const onGoToDashboard = () => {
        navigate(AUTHENTICATED_ROUTES.HOMEPAGE_ROUTE);
    }
    useEffect(() => {
        initParticlesEngine(async (engine) => {
            // you can initiate the tsParticles instance (engine) here, adding custom shapes or presets
            // this loads the tsparticles package bundle, it's the easiest method for getting everything ready
            // starting from v2 you can add only the features you need reducing the bundle size
            //await loadAll(engine);
            //await loadFull(engine);
            await loadSlim(engine);
            //await loadBasic(engine);
        }).then(() => {
            setInit(true);
        });
    }, []);

    const isPublishable = tracking !== undefined && tracking.events.filter(e => e.type === PortfolioTrackEventType.Completed).length !== 0;
    const options = useMemo(
        () => ({
            "fullScreen": {
              "zIndex": 1
            },
            "particles": {
              "color": {
                "value": [
                    Object.keys(theme.palette.leo).filter(n => n.indexOf("color") !== -1).map(n => (theme.palette.leo as any)[n as any])
                ]
              },
              "bounce":{
                "horizontal": {
                    "value":{
                        "min":2,
                        "max": 10
                    }
                }
              },
              "move": {
                "decay": 0.05,
                "direction": "top",
                "enable": true,
                "angle": {
                    value: 90,
                    offset: 0
                },
                "gravity": {
                  "enable": true
                },
                "outModes": {
                  "top": "none",
                  "default": "destroy"
                },
                "speed": {
                  "min": 20,
                  "max": 50
                }
              },
              "number": {
                "value": 200
              },
              "opacity": {
                "value": 1
              },
              "rotate": {
                "value": {
                  "min": 0,
                  "max": 360
                },
                "direction": "random",
                "animation": {
                  "enable": true,
                  "speed": 30,
                  "decay": 0
                }
              },
              "tilt": {
                "direction": "random",
                "enable": true,
                "value": {
                  "min": 0,
                  "max": 360
                },
                "animation": {
                  "enable": true,
                  "speed": 30,
                    "decay": 0
                }
              },
              "size": {
                "value": {
                    "min": 4,
                    "max": 8
                },
                "animation": {
                  "enable": true,
                  "startValue": "min",
                  "count": 1,
                  "speed": 16,
                  "sync": true
                }
              },
              "roll": {
                "mode": "both",
                "darken": {
                  "enable": true,
                  "value": 25
                },
                "enlighten": {
                  "enable": true,
                  "value": 25
                },
                "enable": true,
                "speed": {
                  "min": 5,
                  "max": 25
                }
              },
              "wobble": {
                "distance": 30,
                "enable": true,
                "speed": {
                    "angle": {
                        "min": -15,
                        "max": 15
                    },
                  "min": -7,
                  "max": 7
                }
              },
              "shape": {
                "type": [
                  "square"
                ],
                "options": {}
              }
            },
            "responsive": [
              {
                "maxWidth": 1024,
                "options": {
                  "particles": {
                    "move": {
                      "speed": {
                        "min": 33,
                        "max": 66
                      }
                    }
                  }
                }
              }
            ]
          }),
        [theme.palette.leo],
    );

    return <Box display={"flex"} margin={"0"} justifyContent={"center"} overflow={"auto"}>
        <Box display={"flex"} margin={"50px 56px"} alignItems={"center"} flexDirection={"column"} width={"100%"} maxWidth={"1400px"} gap={"21px"}>
            <Box display={"flex"} width={"100%"} justifyContent={"flex-start"} gap={"16px"} alignItems={"center"}>
              <BackButton />
                <Typography variant='h3'>{isPublishable ? t('PortfolioSummaryPageTitleApproved') : t('PortfolioSummaryPageTitleRunning')}</Typography>
            </Box>
            {tracking === undefined || portfolio === undefined ?
                <CircularProgress />
                :
                <Box display={"flex"} flexDirection={"column"} gap={"21px"} paddingBottom={"20px"}>

                    {init && isPublishable && <Particles
                        id="tsparticles"
                        options={options as any}
                    />}
                    <FundSummaryContent />
                    {isPublishable && <Box display={"flex"} width={"100%"} justifyContent={"flex-end"}>
                        <Button variant="contained" onClick={onGoToDashboard}>{t('PortfolioSummaryPublish')}</Button>
                    </Box>}
                </Box>
            }
        </Box>
    </Box>
}

export default PortfolioSummaryPage;